import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const TakeAssessment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const journeyContext = location.state?.journeyContext;
  const [assessment, setAssessment] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const assessmentRes = await api.assessments.getUserAssessmentById(id);
        const assessmentData = assessmentRes.data.data;
        setAssessment(assessmentData);

        if (assessmentData.can_start === false) {
          toast.info('This assessment is not available yet. Please wait until the scheduled start date.');
          navigate('/user/assessments');
          return;
        }

        const startRes = await api.assessments.start(id);
        setAttempt(startRes.data.data);
        const expiresAt = new Date(startRes.data.data.expires_at).getTime();
        setSecondsLeft(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
      } catch (error) {
        if (error.response?.status === 409) {
          if (journeyContext) {
            try {
              const resultResponse = await api.assessments.getUserResult(id);
              const latestResult = resultResponse.data?.data?.latest_result;
              if (latestResult?.percentage !== undefined) {
                await api.learningJourneys.completeStep(
                  journeyContext.journeyId,
                  journeyContext.stepId,
                  { score: Number(latestResult.percentage) }
                );
              }
            } catch {
              // The result screen will show whether another attempt is required.
            }
          }
          toast.info(error.response?.data?.message || 'Assessment is locked');
          navigate(`/user/assessments/${id}/result`, { state: { journeyContext } });
          return;
        }
        toast.error(error.response?.data?.message || 'Failed to start assessment');
        navigate('/user/assessments');
      }
    };
    bootstrap();
  }, [id, navigate]);

  useEffect(() => {
    if (secondsLeft <= 0 || !attempt) {
      return;
    }
    const timer = setInterval(() => setSecondsLeft((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft, attempt]);

  useEffect(() => {
    if (secondsLeft === 0 && attempt) {
      handleSubmit();
    }
  }, [secondsLeft]);

  const timeLabel = useMemo(() => {
    const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
    const ss = String(secondsLeft % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [secondsLeft]);

  const handleAnswerChange = async (question, value) => {
    const nextAnswers = { ...answers, [question.id]: value };
    setAnswers(nextAnswers);
    try {
      await api.assessments.saveAnswer({
        attempt_id: attempt.id,
        question_id: question.id,
        selected_option_ids: Array.isArray(value) ? value : (question.question_type === 'subjective' ? null : [value]),
        answer_text: question.question_type === 'subjective' ? value : null
      });
    } catch (error) {
      toast.error('Auto-save failed');
    }
  };

  const handleSubmit = async () => {
    if (!attempt || submitting) return;
    try {
      setSubmitting(true);
      setEvaluating(true);

      const questions = assessment?.questions || [];
      for (const question of questions) {
        const answerValue = answers[question.id];
        const isMulti = question.question_type === 'multiple_choice';
        const isSubjective = question.question_type === 'subjective';

        if (isSubjective) {
          if (typeof answerValue === 'string' && answerValue.trim()) {
            await api.assessments.saveAnswer({
              attempt_id: attempt.id,
              question_id: question.id,
              selected_option_ids: null,
              answer_text: answerValue
            });
          }
          continue;
        }

        if (isMulti) {
          if (Array.isArray(answerValue) && answerValue.length > 0) {
            await api.assessments.saveAnswer({
              attempt_id: attempt.id,
              question_id: question.id,
              selected_option_ids: answerValue,
              answer_text: null
            });
          }
          continue;
        }

        if (answerValue !== null && answerValue !== undefined && answerValue !== '') {
          await api.assessments.saveAnswer({
            attempt_id: attempt.id,
            question_id: question.id,
            selected_option_ids: [answerValue],
            answer_text: null
          });
        }
      }

      const submitResponse = await api.assessments.submit(attempt.id);
      const assessmentResult = submitResponse.data?.data;
      if (journeyContext && assessmentResult?.percentage !== undefined) {
        try {
          await api.learningJourneys.completeStep(
            journeyContext.journeyId,
            journeyContext.stepId,
            { score: Number(assessmentResult.percentage) }
          );
        } catch (journeyError) {
          toast.info(journeyError.response?.data?.message || 'Pass the assessment to unlock the next module');
        }
      }
      toast.success('Assessment submitted successfully');
      navigate(`/user/assessments/${id}/result`, { state: { journeyContext } });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
      setEvaluating(false);
    }
  };

  if (!assessment || !attempt) {
    return <div className="text-center py-8"><Spinner /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {evaluating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto mb-3"></div>
            <h5 className="mb-1">Evaluating your assessment</h5>
            <p className="text-sm text-gray-600 mb-0">Please wait while we calculate your result.</p>
          </div>
        </div>
      )}

      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{assessment.title}</h3>
                <p className="text-sm text-gray-600 mb-0">{assessment.description || 'Answer all questions carefully before submitting.'}</p>
              </div>
            </div>
            <div className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-red-100 text-red-700 font-bold">
              Time Left: {timeLabel}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {(assessment.questions || []).map((question, index) => (
          <div key={question.id} className="bg-white rounded-2xl border border-purple-100 shadow-lg p-4 sm:p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold text-sm flex items-center justify-center mt-0.5">
                {index + 1}
              </div>
              <p className="mb-0 text-gray-900 font-medium">{question.question_text}</p>
            </div>
          {question.question_type === 'subjective' ? (
            <Form.Control
              as="textarea"
              rows={4}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question, e.target.value)}
              className="rounded-xl border-purple-200 focus:ring-purple-500"
              placeholder="Type your answer here..."
            />
          ) : (
            <div className="space-y-2">
              {(question.options || []).map((opt) => (
                <Form.Check
                  key={opt.id}
                  type={question.question_type === 'multiple_choice' ? 'checkbox' : 'radio'}
                  label={opt.option_text}
                  name={`q-${question.id}`}
                  className="p-2 rounded-lg hover:bg-purple-50 transition-all"
                  onChange={(e) => {
                    if (question.question_type === 'multiple_choice') {
                      const prev = Array.isArray(answers[question.id]) ? answers[question.id] : [];
                      const next = e.target.checked ? [...prev, opt.id] : prev.filter((item) => item !== opt.id);
                      handleAnswerChange(question, next);
                      return;
                    }
                    handleAnswerChange(question, opt.id);
                  }}
                />
              ))}
            </div>
          )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-center">
        <Button
          bsPrefix="custom-hamburger"
          className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm sm:text-base px-8 py-3 min-w-52"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Assessment'}
        </Button>
      </div>
    </div>
  );
};

export default TakeAssessment;
